import type { SeasonHuntState, WordHuntState } from "../core/SaveManager";
import type { BoxReward } from "./Shop";

/** Word Hunt: one word a day, its letters appear in runs one at a time, in order. Streaks pay more. */
export const WORDS = ["SOLANA", "EXCEL", "BALTIC", "VILNIUS", "RIGA", "TALLINN", "DEGEN", "WALLET", "STAKE", "HODL", "BLOCK", "SHEET",
  "PIXEL", "RUNNER", "HOVER", "MAGNET", "TRUCK", "HIGHWAY", "ELON", "LEDGER", "MINT", "VALIDATOR", "BRIDGE", "AIRDROP", "ROADSTER"];

export function dayKey(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function yesterdayKey(date = new Date()): string {
  const d = new Date(date);
  d.setDate(d.getDate() - 1);
  return dayKey(d);
}

function wordFor(key: string): string {
  let h = 0;
  for (const c of key) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return WORDS[h % WORDS.length];
}

/** Makes sure the hunt is today's; a missed day resets the streak. */
export function ensureWordHunt(state: WordHuntState, today = dayKey()): WordHuntState {
  if (state.date !== today) {
    state.date = today;
    state.word = wordFor(today);
    state.collected = 0;
    if (state.lastCompleted && state.lastCompleted !== yesterdayKey() && state.lastCompleted !== today) state.streak = 0;
  }
  return state;
}

export function wordHuntDone(state: WordHuntState, today = dayKey()): boolean {
  return state.lastCompleted === today;
}

/** The next letter the runs should spawn, or null when today's word is done. */
export function nextLetter(state: WordHuntState, today = dayKey()): string | null {
  ensureWordHunt(state, today);
  if (wordHuntDone(state, today)) return null;
  return state.word[state.collected] ?? null;
}

/** Streak rewards ⚠ wiki: day 4 pays 1,500 coins, day 5+ a Super Mystery Box; the first days are ours. */
export function wordHuntReward(streak: number): BoxReward {
  if (streak >= 5) return { kind: "coins", amount: 0 };          // the Super Mystery Box is granted separately
  if (streak === 4) return { kind: "coins", amount: 1500 };
  if (streak === 3) return { kind: "board", amount: 1 };
  if (streak === 2) return { kind: "keys", amount: 1 };
  return { kind: "coins", amount: 500 };
}

/** Letters picked up in a run: only the next needed letter advances the word. */
export function collectLetters(state: WordHuntState, letters: string[], today = dayKey()): { completed: boolean; streak: number } {
  ensureWordHunt(state, today);
  if (wordHuntDone(state, today)) return { completed: false, streak: state.streak };
  for (const letter of letters) {
    if (state.word[state.collected] === letter) state.collected += 1;
  }
  if (state.collected >= state.word.length) {
    state.streak = state.lastCompleted === yesterdayKey() ? state.streak + 1 : 1;
    state.lastCompleted = today;
    state.completedTotal += 1;
    return { completed: true, streak: state.streak };
  }
  return { completed: false, streak: state.streak };
}

/** Season Hunt: tokens picked up in runs climb tiers; each tier pays once. ⚠ ours */
export const seasonTiers: { tokens: number; reward: BoxReward }[] = [
  { tokens: 10, reward: { kind: "coins", amount: 500 } }, { tokens: 25, reward: { kind: "board", amount: 1 } },
  { tokens: 45, reward: { kind: "keys", amount: 2 } }, { tokens: 70, reward: { kind: "headstart", amount: 1 } },
  { tokens: 100, reward: { kind: "coins", amount: 2000 } }, { tokens: 135, reward: { kind: "booster", amount: 1 } },
  { tokens: 175, reward: { kind: "keys", amount: 2 } }, { tokens: 220, reward: { kind: "board", amount: 2 } },
  { tokens: 270, reward: { kind: "coins", amount: 5000 } }, { tokens: 325, reward: { kind: "shield", amount: 2 } },
  { tokens: 385, reward: { kind: "keys", amount: 3 } }, { tokens: 450, reward: { kind: "coins", amount: 8000 } },
  { tokens: 520, reward: { kind: "headstart", amount: 2 } }, { tokens: 600, reward: { kind: "keys", amount: 3 } },
  { tokens: 700, reward: { kind: "coins", amount: 12000 } }, { tokens: 820, reward: { kind: "board", amount: 3 } },
  { tokens: 960, reward: { kind: "keys", amount: 5 } }, { tokens: 1120, reward: { kind: "coins", amount: 20000 } },
  { tokens: 1300, reward: { kind: "booster", amount: 3 } }, { tokens: 1500, reward: { kind: "keys", amount: 5 } }
];

export function seasonProgress(state: SeasonHuntState): { tier: number; next: number | null; into: number } {
  let tier = 0;
  while (tier < seasonTiers.length && state.tokens >= seasonTiers[tier].tokens) tier += 1;
  const next = tier < seasonTiers.length ? seasonTiers[tier].tokens : null;
  const prev = tier > 0 ? seasonTiers[tier - 1].tokens : 0;
  return { tier, next, into: next ? (state.tokens - prev) / (next - prev) : 1 };
}

/** Rewards of tiers reached since the last claim. */
export function claimSeason(state: SeasonHuntState): BoxReward[] {
  const { tier } = seasonProgress(state);
  const rewards: BoxReward[] = [];
  for (let t = state.claimedTier; t < tier; t++) rewards.push(seasonTiers[t].reward);
  state.claimedTier = tier;
  return rewards;
}
